/**
 * PvP Leaderboard Screen
 *
 * Displays top 100 players ranked by rating with:
 * - Rank number
 * - Profile icon (clickable for profile summary)
 * - Username
 * - Division badge
 * - Rating
 * - Win/Loss record
 */

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Trophy, Medal, Crown } from 'lucide-react';
import { motion } from 'framer-motion';
import { useGame } from '../contexts/GameContext';
import { useAuth } from '../contexts/AuthContext';
import { apiGetPvPLeaderboard } from '../services/apiService';
import ProfileIcon from '../components/ProfileIcon';
import UserProfileModal from '../components/UserProfileModal';

const PvPLeaderboardScreen = () => {
  const { setGameState } = useGame();
  const { authToken, user } = useAuth();

  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);

  useEffect(() => {
    const loadLeaderboard = async () => {
      if (!authToken) {
        setLoading(false);
        return;
      }

      try {
        const data = await apiGetPvPLeaderboard(authToken);
        if (data?.leaderboard) {
          setLeaderboard(data.leaderboard);
        }
      } catch (err) {
        console.error('Failed to load leaderboard:', err);
      } finally {
        setLoading(false);
      }
    };

    loadLeaderboard();
  }, [authToken]);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown size={20} className="text-yellow-500" />;
    if (rank === 2) return <Medal size={20} className="text-gray-400" />;
    if (rank === 3) return <Medal size={20} className="text-amber-600" />;
    return null;
  };

  const getDivisionColor = (division) => {
    switch (division) {
      case 'Master': return '#9333ea';
      case 'Diamond': return '#06b6d4';
      case 'Platinum': return '#6b7280';
      case 'Gold': return '#eab308';
      case 'Silver': return '#94a3b8';
      case 'Bronze': return '#b45309';
      default: return '#6b7280';
    }
  };

  const handleProfileClick = (player) => {
    setSelectedProfile({
      userId: player.userId,
      username: player.username,
      profileIcon: player.profileIcon
    });
  };

  return (
    <div className="min-h-screen bg-pocket-bg p-4">
      {/* Header */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="sticky top-0 z-10 bg-white shadow-card rounded-2xl mb-4 max-w-4xl mx-auto"
      >
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setGameState('pvp')}
            className="p-2 text-pocket-text-light hover:text-pocket-text hover:bg-pocket-bg rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <Trophy size={20} className="text-amber-500" />
            <span className="font-bold text-pocket-text">Leaderboard</span>
          </div>
          <div className="w-10" />
        </div>
      </motion.header>

      <div className="max-w-4xl mx-auto">
        {loading ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-card p-8 text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-pocket-bg flex items-center justify-center">
              <Trophy size={32} className="text-pocket-text-light animate-pulse" />
            </div>
            <p className="text-pocket-text-light">Loading leaderboard...</p>
          </motion.div>
        ) : leaderboard.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-card p-8 text-center"
          >
            <Trophy size={48} className="mx-auto mb-4 text-pocket-text-light opacity-30" />
            <p className="text-pocket-text-light">No leaderboard data available</p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-card overflow-hidden"
          >
            {/* Top 3 Podium */}
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-6 border-b-2 border-amber-200">
              <div className="flex items-end justify-center gap-4 mb-2">
                {/* 2nd Place */}
                {leaderboard[1] && (
                  <div className="flex flex-col items-center">
                    <div className="relative mb-2 cursor-pointer" onClick={() => handleProfileClick(leaderboard[1])}>
                      <ProfileIcon
                        icon={leaderboard[1].profileIcon}
                        size={48}
                        showBorder={true}
                        className="ring-4 ring-gray-300"
                      />
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-gray-700">2</span>
                      </div>
                    </div>
                    <div className="bg-gray-200 rounded-t-xl px-3 py-6 min-w-[80px]">
                      <p className="text-xs font-bold text-center text-gray-700 truncate">{leaderboard[1].username}</p>
                      <p className="text-center text-sm font-bold text-gray-600">{leaderboard[1].rating}</p>
                    </div>
                  </div>
                )}

                {/* 1st Place */}
                {leaderboard[0] && (
                  <div className="flex flex-col items-center">
                    <Crown size={24} className="text-yellow-500 mb-1" />
                    <div className="relative mb-2 cursor-pointer" onClick={() => handleProfileClick(leaderboard[0])}>
                      <ProfileIcon
                        icon={leaderboard[0].profileIcon}
                        size={56}
                        showBorder={true}
                        className="ring-4 ring-yellow-400"
                      />
                      <div className="absolute -top-2 -right-2 w-7 h-7 bg-yellow-400 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-yellow-900">1</span>
                      </div>
                    </div>
                    <div className="bg-yellow-300 rounded-t-xl px-3 py-8 min-w-[90px]">
                      <p className="text-xs font-bold text-center text-yellow-900 truncate">{leaderboard[0].username}</p>
                      <p className="text-center text-base font-bold text-yellow-800">{leaderboard[0].rating}</p>
                    </div>
                  </div>
                )}

                {/* 3rd Place */}
                {leaderboard[2] && (
                  <div className="flex flex-col items-center">
                    <div className="relative mb-2 cursor-pointer" onClick={() => handleProfileClick(leaderboard[2])}>
                      <ProfileIcon
                        icon={leaderboard[2].profileIcon}
                        size={48}
                        showBorder={true}
                        className="ring-4 ring-amber-500"
                      />
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-amber-900">3</span>
                      </div>
                    </div>
                    <div className="bg-amber-300 rounded-t-xl px-3 py-6 min-w-[80px]">
                      <p className="text-xs font-bold text-center text-amber-900 truncate">{leaderboard[2].username}</p>
                      <p className="text-center text-sm font-bold text-amber-700">{leaderboard[2].rating}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Rest of Leaderboard */}
            <div className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
              {leaderboard.slice(3).map((player, index) => {
                const isCurrentUser = user && player.userId === user.id;
                const actualRank = index + 4;

                return (
                  <motion.div
                    key={player.userId}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(index * 0.02, 0.5) }}
                    className={`flex items-center gap-3 p-3 hover:bg-pocket-bg transition-colors ${
                      isCurrentUser ? 'bg-blue-50' : ''
                    }`}
                  >
                    {/* Rank */}
                    <div className="w-8 text-center">
                      <span className={`text-sm font-bold ${
                        isCurrentUser ? 'text-blue-600' : 'text-pocket-text-light'
                      }`}>
                        {actualRank}
                      </span>
                    </div>

                    {/* Profile Icon */}
                    <div
                      className="cursor-pointer"
                      onClick={() => handleProfileClick(player)}
                    >
                      <ProfileIcon
                        icon={player.profileIcon}
                        size={40}
                        showBorder={true}
                        className={isCurrentUser ? 'ring-2 ring-blue-400' : ''}
                      />
                    </div>

                    {/* Username & Division */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-bold text-sm truncate ${
                        isCurrentUser ? 'text-blue-600' : 'text-pocket-text'
                      }`}>
                        {player.username}
                        {isCurrentUser && <span className="ml-1 text-xs">(You)</span>}
                      </p>
                      <span
                        className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold text-white"
                        style={{ backgroundColor: getDivisionColor(player.division) }}
                      >
                        {player.division}
                      </span>
                    </div>

                    {/* Rating */}
                    <div className="text-right">
                      <p className="text-lg font-bold text-pocket-text">{player.rating}</p>
                      <p className="text-[10px] text-pocket-text-light">
                        {player.wins}W - {player.losses}L
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>

      {/* Profile Modal */}
      {selectedProfile && (
        <UserProfileModal
          userId={selectedProfile.userId}
          username={selectedProfile.username}
          profileIcon={selectedProfile.profileIcon}
          isOpen={!!selectedProfile}
          onClose={() => setSelectedProfile(null)}
        />
      )}
    </div>
  );
};

export default PvPLeaderboardScreen;
